import React, { Component, createRef } from 'react';
import Grid from '@mui/material/Grid';
import { withStyles } from '@mui/styles';
import { Tooltip } from '@mui/material';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { TipTapEditor } from '../TipTap';

const useStyles = () => ({
  gridJodit: {},
  gridJoditTier: {},
});

/**
 * TextEditorExtraPlaceholder - Rich text editor with $ and ! placeholders
 * 
 * Full toolbar, supports both workflow ($) and form (!) placeholders.
 * Used in TaskInfo for task descriptions with form field references.
 */
class TextEditor extends Component {
  editorRef = createRef();

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      content: '',
    };
    if (this.props.editorFocus) {
      this.props.editorFocus();
    }
  }

  componentDidMount() {
    this.setState({
      content: this.normalizeContent(this.props.html),
    });
  }

  normalizeContent(html) {
    let content = html;

    if (content) {
      // Normalize table styling
      if (content.includes('table')) {
        content = content
          .replace(/(<table style=")/g, () => {
            return '<table border="1" style="border-collapse:collapse; ';
          })
          .replace(/(border-color:)/g, 'border: 1px solid');
      }
      // Handle newlines and table cell padding
      content = content
        .replace(/(?:\r\n|\r|\n)/g, '<br>')
        .replace(/(<td style="width: [0-9.]+%;">)/g, val => {
          let width = val.match(/([0-9.]+)/g)[0];
          return `<td style="width: ${width}%; padding: 4px;">`;
        });
    } else {
      content = '';
    }

    return content;
  }

  componentDidUpdate(prevProps) {
    const { placeholder } = this.props;
    if (placeholder && placeholder.length !== prevProps.placeholder?.length) {
      this.insertPlaceholder(placeholder[placeholder.length - 1]);
    }
  }

  insertPlaceholder = (placeholder) => {
    if (placeholder && this.editorRef.current) {
      this.editorRef.current.insertPlaceholder(placeholder);
    }
  };

  handleChange = (value) => {
    this.setState({ content: value });
  };

  handleBlur = (value) => {
    // Normalize table HTML on output
    let normalizedValue = value.replace(/(?:\n)/g, '');
    normalizedValue = normalizedValue
      .replace(/(<table style=")/g, () => {
        return '<table border="1" style="border-collapse:collapse; ';
      })
      .replace(/(border-color:)/g, 'border: 1px solid')
      .replace(/(<td style="width: [0-9.]+%;">)/g, val => {
        let width = val.match(/([0-9.]+)/g)[0];
        return `<td style="width: ${width}%; padding: 4px;">`;
      });

    if (this.props.onChange) {
      this.props.onChange(normalizedValue);
    }
  };

  handleFocus = () => {
    if (this.props.editorFocus) {
      this.props.editorFocus();
    }
  };

  // Combine regular placeholders and extra placeholders
  getCombinedPlaceholders = () => {
    const { availablePlaceholders, extraPlaceholders } = this.props;
    const combined = [];
    
    // Add regular placeholders (for $ trigger)
    if (availablePlaceholders) {
      const placeholdersList = availablePlaceholders[0]?.placeholders || availablePlaceholders;
      combined.push(...placeholdersList);
    }
    
    // Add extra placeholders (for ! trigger)
    if (extraPlaceholders && Array.isArray(extraPlaceholders)) {
      combined.push(...extraPlaceholders);
    }
    
    return combined;
  };

  render() {
    const { classes, disableTooltip = false } = this.props;
    const { content, loading } = this.state;

    if (loading) {
      return (
        <div className="my-nice-tab-container">
          <div className="loading-state" />
        </div>
      );
    }

    return (
      <Tooltip
        placement="bottom"
        title={disableTooltip !== true ? 'Type "$" to view and insert placeholders' : ''}
        disableHoverListener={disableTooltip}
        disableFocusListener={disableTooltip}
        disableTouchListener={disableTooltip}
        enterDelay={1200}
        enterNextDelay={1200}
      >
        <Grid container onClick={this.handleFocus} spacing={3}>
          <Grid
            item
            className={
              useProjectStore.getState().tier === 'FREE' ? classes.gridJodit : classes.gridJoditTier
            }
            xs={12}
          >
            <TipTapEditor
              ref={this.editorRef}
              value={content}
              onChange={this.handleChange}
              onBlur={this.handleBlur}
              onFocus={this.handleFocus}
              showToolbar={true}
              triggerChars={['$', '!']}
              availablePlaceholders={this.getCombinedPlaceholders()}
              projectId={useProjectStore.getState().project_id}
              placeholder=""
            />
          </Grid>
        </Grid>
      </Tooltip>
    );
  }
}

export default withStyles(useStyles)(TextEditor);
